// StudentSignup.component: student register wizard; account step hides header title "지망생 정보 등록", info steps show it with step index.
// Mistake: OAuth email-link confirm lived in a modal; dedicated /register/oauth-link route owns that flow now.
// Mistake: term modal used iframe; signup term list from getTermListByType already includes title/text for inline display.
// Mistake: use Term.component rule—version>=2 is markdown (ReactMarkdown), else HTML (dangerouslySetInnerHTML); mixed term types coexist in the list.
// Mistake: OAuth account step allowed editing email; social signup binds username to provider email—keep the field read-only when oauthSignupMeta is set.
// Mistake: sticky header for info steps used a Korean-only template; use studentSignupWizard.txtInfoRegistrationProgress for all locales.
// Mistake: wizard step blocks must end with `)}` after `</>`; omitting it breaks TSX parse and hides the default export.
import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";

import * as commonData from "data/common.data";
import { getCurrentLangName } from "helpers/String.helper";
import Header from "./Header.component";

type StudentSignupRegisterStep = "account" | "profile" | "basic" | "specialty" | "contact" | "confirm";
type StudentLanguageRow = { id: string; languageCode: string; level: string };

/** 지망생 정보 등록 5단계: 프로필 → 기본 → 특기 → 연락처 → 확인 (account은 별도) */
const INFO_REG_STEPS_ORDER: readonly StudentSignupRegisterStep[] = ["profile", "basic", "specialty", "contact", "confirm"];
const INFO_REG_STEP_TOTAL = 5;
const STEP_FORM_LEAD_KEYS: Record<StudentSignupRegisterStep, { titleKey: string; descKey: string }> = {
    account: { titleKey: "studentSignupWizard.stepAccountTitle", descKey: "studentSignupWizard.stepAccountDesc" },
    profile: { titleKey: "studentSignupWizard.stepProfileTitle", descKey: "studentSignupWizard.stepProfileDesc" },
    basic: { titleKey: "studentSignupWizard.stepBasicTitle", descKey: "studentSignupWizard.stepBasicDesc" },
    specialty: { titleKey: "studentSignupWizard.stepSpecialtyTitle", descKey: "studentSignupWizard.stepSpecialtyDesc" },
    contact: { titleKey: "studentSignupWizard.stepContactTitle", descKey: "studentSignupWizard.stepContactDesc" },
    confirm: { titleKey: "studentSignupWizard.stepConfirmTitle", descKey: "studentSignupWizard.stepConfirmDesc" },
};

const StepFormLead = ({ step }: { step: StudentSignupRegisterStep }) => {
    const k = STEP_FORM_LEAD_KEYS[step];
    return (
        <div className="mb-6 text-left">
            <h3 className="text-xl font-semibold text-gray-900">{getLanguageValue(PAGE_TYPE.COMMON, k.titleKey)}</h3>
            <p className="mt-1 text-sm text-gray-500">{getLanguageValue(PAGE_TYPE.COMMON, k.descKey)}</p>
        </div>
    );
};

const proficiencyLabel = (levelCode: string, languageLevelList: any[]) => {
    const levelItem = languageLevelList.find((item: any) => item.code === levelCode);
    return levelItem ? getCurrentLangName(levelItem.names) : levelCode;
};

/** 최종확인: 레이블 1줄 + 값 1줄(들여쓰기) */
const ConfirmPreviewRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="mb-3 last:mb-0">
        <div className="text-gray-500">{label}</div>
        <div className="pl-4 text-gray-900 break-words">{children}</div>
    </div>
);

type StudentSignupTermModalData = { title: string; text: string; version: number };

/** Scroll + typography for both HTML-injected and ReactMarkdown output (matches Term.component: version>=2 → markdown). */
const STUDENT_SIGNUP_TERM_MODAL_BODY_CLASS =
    "max-h-[min(70vh,640px)] min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-2 text-sm text-gray-800 " +
    "[&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:text-xl [&_h1]:font-semibold [&_h1]:text-gray-900 " +
    "[&_h2]:mt-3 [&_h2]:mb-2 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-gray-900 " +
    "[&_h3]:mt-2 [&_h3]:font-medium [&_p]:mb-3 [&_p]:leading-relaxed [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 " +
    "[&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-0.5 [&_a]:text-[#CF3688] [&_a]:underline [&_strong]:font-semibold " +
    "[&_code]:rounded [&_code]:bg-gray-100 [&_code]:px-1 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-gray-100 [&_pre]:p-3 " +
    "[&_table]:w-full [&_table]:border-collapse [&_th]:border [&_td]:border [&_th]:px-2 [&_td]:px-2 [&_th]:py-1 [&_td]:py-1";

const StudentSignupTermModal = ({ term, onClose }: { term: StudentSignupTermModalData; onClose: () => void }) => {
    const { title, text, version } = term;
    const isMarkdown = Number(version) >= 2;
    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="student-signup-term-modal-title"
            onClick={onClose}
        >
            <div
                className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
                    <h3
                        id="student-signup-term-modal-title"
                        className="min-w-0 flex-1 truncate text-base font-semibold text-gray-900"
                    >
                        {title}
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="shrink-0 rounded-lg px-2 py-1 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    >
                        {getLanguageValue(PAGE_TYPE.COMMON, "login.btnCloseEmailModal")}
                    </button>
                </div>
                {isMarkdown ? (
                    <div className={STUDENT_SIGNUP_TERM_MODAL_BODY_CLASS}>
                        <ReactMarkdown>{text || ""}</ReactMarkdown>
                    </div>
                ) : (
                    <div
                        className={`term-html-content ${STUDENT_SIGNUP_TERM_MODAL_BODY_CLASS}`}
                        dangerouslySetInnerHTML={{ __html: text || "" }}
                    />
                )}
            </div>
        </div>
    );
};

const StudentSignupComponent = (props: any) => {
    const {
        btnRef,
        currentStep,
        oauthSignupMeta,
        accountParams,
        studentParams,
        parentEmail,
        setParentEmail,
        checkIsUnder14,
        imageInfo,
        termList,
        isOnNetworking,
        selectedSpecialtyCodes,
        languageRows,
        addLanguageRow,
        removeLanguageRow,
        updateLanguageRow,
        onProfileImageFileChange,
        onChangeInput,
        onBlurUsername,
        onBlurPassword,
        onBlurConfirmedPassword,
        usernameValidationType,
        usernameValidationMessage,
        passwordValidationType,
        passwordValidationMessage,
        confirmedPasswordValidationType,
        confirmedPasswordValidationMessage,
        isAccountNextEnabled,
        isProfileNextEnabled,
        onBlurProfileSlug,
        profileSlugValidationType,
        profileSlugValidationMessage,
        onChangeCheckbox,
        onClickBtnCancelSignup,
        onWizardNext,
        onWizardBack,
        onClickBtnSignup,
        headerBackUrl,
        isInfoRegistrationFlow,
        onDevAutofillClick,
    } = props;

    const isOauthAccountStep = !!oauthSignupMeta;

    const [termModal, setTermModal] = useState<StudentSignupTermModalData | null>(null);

    const appDomainBase = (process.env.REACT_APP_DOMAIN || "").replace(/\/+$/, "");
    const handlerProfileUrlPrefix = appDomainBase ? `${appDomainBase}/wannabe/info/` : "/wannabe/info/";

    const langList = commonData.getLanguageCodeList();
    const languageLevelList = commonData.getCommonCodeListByParent("languageLevel");
    const specialtyList = commonData.getCommonCodeListByParent("specialty");
    const countryItem = commonData.getCountryCodeList().find((c: any) => c.code === studentParams.countryCode);
    const mbtiItem = commonData.getMbtiCodeList().find((m: any) => m.code === studentParams.mbtiCode);
    const contactTypeItem = commonData.getCommonCodeListByParent("contactType").find((c: any) => c.code === studentParams.contactTypeCode);

    const infoStepIndex = INFO_REG_STEPS_ORDER.indexOf(currentStep as StudentSignupRegisterStep);
    const infoHeaderTitle =
        currentStep === "account" || infoStepIndex < 0
            ? ""
            : getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.txtInfoRegistrationProgress")
                  .replace("%CURRENT%", String(infoStepIndex + 1))
                  .replace("%TOTAL%", String(INFO_REG_STEP_TOTAL));

    const renderFooter = (opts: { showCancelOnLeft?: boolean; showBack?: boolean; primaryLabel: string; onPrimary: () => void; primaryDisabled?: boolean }) => {
        const { showCancelOnLeft, showBack, primaryLabel, onPrimary, primaryDisabled } = opts;
        return (
            <div className="flex w-full flex-row items-center justify-between gap-3 pt-6">
                <div className="flex items-center gap-2 min-w-0">
                    {showCancelOnLeft && (
                        <button
                            type="button"
                            onClick={onClickBtnCancelSignup}
                            className="px-6 py-3 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors shrink-0"
                        >
                            {getLanguageValue(PAGE_TYPE.COMMON, "studentSignup.btnCancel")}
                        </button>
                    )}
                    {showBack && (
                        <button
                            type="button"
                            onClick={onWizardBack}
                            className="px-6 py-3 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors shrink-0"
                        >
                            {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.btnBack")}
                        </button>
                    )}
                </div>
                <button
                    type="button"
                    onClick={onPrimary}
                    disabled={!!primaryDisabled}
                    className="shrink-0 px-6 py-3 text-white bg-[#CF3688] rounded-xl hover:bg-[#B52E74] transition-colors disabled:opacity-50"
                >
                    {primaryLabel}
                </button>
            </div>
        );
    };

    const sectionShell = (children: React.ReactNode) => (
        <section className="bg-white rounded-2xl shadow-sm overflow-hidden mb-4">
            <div className="p-6">{children}</div>
        </section>
    );

    const termsAgreementBlock = (
        <div className="space-y-4">
            {termList.map((item: any, idx: number) => (
                <div key={`term-${idx}`} className="flex items-center justify-between gap-2">
                    <label className="flex items-center space-x-3">
                        <input
                            type="checkbox"
                            onChange={(evt) => onChangeCheckbox(null, idx, evt.target.checked)}
                            checked={item.checked}
                            className="h-4 w-4 text-[#CF3688] focus:ring-[#CF3688] rounded"
                        />
                        <span className="text-sm text-gray-700">
                            ({item.isRequired
                                ? getLanguageValue(PAGE_TYPE.COMMON, "studentSignupTerms.txtRequired")
                                : getLanguageValue(PAGE_TYPE.COMMON, "studentSignupTerms.txtOptional")}
                            ) {item.title}
                        </span>
                    </label>
                    {item.termsTypeCode !== "termsType:over14" && (
                        <button
                            type="button"
                            onClick={() =>
                                setTermModal({
                                    title: String(item.title || ""),
                                    text: String(item.text ?? ""),
                                    version: Number(item.version) || 0,
                                })
                            }
                            className="text-sm text-[#CF3688] hover:text-[#B52E74] transition-colors shrink-0"
                        >
                            ({getLanguageValue(PAGE_TYPE.COMMON, "studentSignupTerms.txtShow")})
                        </button>
                    )}
                </div>
            ))}
            <div className="pt-4 border-t">
                <label className="flex items-center space-x-3">
                    <input
                        type="checkbox"
                        onChange={(evt) => onChangeCheckbox(null, "all", evt.target.checked)}
                        checked={!termList.find((i: any) => !i.checked)}
                        className="h-4 w-4 text-[#CF3688] focus:ring-[#CF3688] rounded"
                    />
                    <span className="text-sm font-medium text-gray-900">
                        {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupTerms.txtAgreeWithAllTerms")}
                    </span>
                </label>
            </div>
        </div>
    );

    return (
        <>
            {termModal ? <StudentSignupTermModal term={termModal} onClose={() => setTermModal(null)} /> : null}
            <Header
                backUrl={headerBackUrl ?? "/signup"}
                hideBackButton={!!isInfoRegistrationFlow}
                titleText={infoHeaderTitle}
                onTitleClick={infoHeaderTitle ? onDevAutofillClick : undefined}
            />
            <main className="min-h-screen bg-gray-50 pt-10">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-6 lg:pt-6">
                    {!isInfoRegistrationFlow && (
                        <div className="mb-6 text-left">
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                                {getLanguageValue(PAGE_TYPE.COMMON, "studentSignup.txtTitle")}
                            </h2>
                        </div>
                    )}
                    {currentStep !== "account" && (
                        <div className="mb-2">
                            <StepFormLead step={currentStep as StudentSignupRegisterStep} />
                        </div>
                    )}

                    {currentStep === "account" && (
                        <>
                            {sectionShell(
                                <>
                                    <div className="space-y-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                <span className="text-red-500">*</span>
                                                {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAccount.txtEmail")}
                                            </label>
                                            <input
                                                type="email"
                                                value={accountParams.username}
                                                readOnly={isOauthAccountStep}
                                                onChange={(evt) => onChangeInput("username", evt.target.value)}
                                                onBlur={() => {
                                                    if (!isOauthAccountStep) {
                                                        void onBlurUsername();
                                                    }
                                                }}
                                                className={`block w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-base transition-colors ${
                                                    isOauthAccountStep
                                                        ? "bg-gray-100 text-gray-800 cursor-not-allowed"
                                                        : "focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688]"
                                                }`}
                                                placeholder={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAccount.txtEmailPlaceholder")}
                                                autoComplete="email"
                                                aria-readonly={isOauthAccountStep || undefined}
                                            />
                                            {usernameValidationMessage && (
                                                <p
                                                    className={`mt-2 text-sm ${usernameValidationType === "success"
                                                        ? "text-green-600"
                                                        : usernameValidationType === "checking"
                                                          ? "text-gray-500"
                                                          : "text-red-500"
                                                        }`}
                                                >
                                                    {usernameValidationMessage}
                                                </p>
                                            )}
                                        </div>
                                        {!oauthSignupMeta && (
                                            <>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        <span className="text-red-500">*</span>
                                                        {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAccount.txtPassword")}
                                                    </label>
                                                    <input
                                                        type="password"
                                                        value={accountParams.password}
                                                        onChange={(evt) => onChangeInput("password", evt.target.value)}
                                                        onBlur={() => {
                                                            onBlurPassword();
                                                        }}
                                                        className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688] text-base transition-colors"
                                                        placeholder={getLanguageValue(
                                                            PAGE_TYPE.COMMON,
                                                            "studentSignupAccount.txtPasswordPlaceholder"
                                                        )}
                                                        minLength={8}
                                                        maxLength={16}
                                                    />
                                                    {passwordValidationMessage && (
                                                        <p className={`mt-2 text-sm ${passwordValidationType === "error" ? "text-red-500" : "text-green-600"}`}>
                                                            {passwordValidationMessage}
                                                        </p>
                                                    )}
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        <span className="text-red-500">*</span>
                                                        {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAccount.txtConfirmPassword")}
                                                    </label>
                                                    <input
                                                        type="password"
                                                        value={accountParams.confirmedPassword}
                                                        onChange={(evt) => onChangeInput("confirmedPassword", evt.target.value)}
                                                        onBlur={() => {
                                                            onBlurConfirmedPassword();
                                                        }}
                                                        className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688] text-base transition-colors"
                                                        placeholder={getLanguageValue(
                                                            PAGE_TYPE.COMMON,
                                                            "studentSignupAccount.txtConfirmPasswordPlaceholder"
                                                        )}
                                                        minLength={8}
                                                        maxLength={16}
                                                    />
                                                    {confirmedPasswordValidationMessage && (
                                                        <p
                                                            className={`mt-2 text-sm ${confirmedPasswordValidationType === "error"
                                                                ? "text-red-500"
                                                                : "text-green-600"
                                                                }`}
                                                        >
                                                            {confirmedPasswordValidationMessage}
                                                        </p>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                            {sectionShell(
                                <>
                                    {termsAgreementBlock}
                                </>
                            )}
                            {renderFooter({
                                showCancelOnLeft: true,
                                showBack: false,
                                primaryLabel: getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.btnNext"),
                                primaryDisabled: isOnNetworking || !isAccountNextEnabled,
                                onPrimary: () => {
                                    void onWizardNext();
                                },
                            })}
                        </>
                    )}

                    {currentStep === "profile" && (
                        <>
                            {sectionShell(
                                <>
                                    <div className="space-y-6">
                                        <div className="flex justify-center">
                                            <div className="relative w-40 h-40">
                                                <div
                                                    className="w-full h-full rounded-full bg-center bg-cover"
                                                    style={{
                                                        backgroundImage: `url(${imageInfo?.url || "./../../imgs/icons/camera.png"})`,
                                                    }}
                                                />
                                                <input
                                                    type="file"
                                                    id="input-file-img-profile"
                                                    className="hidden"
                                                    onChange={(evt) => onProfileImageFileChange(evt)}
                                                    accept="image/*"
                                                />
                                                <label
                                                    htmlFor="input-file-img-profile"
                                                    className="absolute bottom-0 right-0 bg-[#CF3688] text-white p-1.5 rounded-full cursor-pointer hover:bg-[#B52E74] transition-colors"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                </label>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                <span className="text-red-500">*</span>
                                                {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupEssential.txtCountry")}
                                            </label>
                                            <select
                                                value={studentParams.countryCode}
                                                onChange={(evt) => onChangeInput("countryCode", evt.target.value)}
                                                className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688] text-base transition-colors"
                                            >
                                                <option value="" disabled>
                                                    {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupEssential.txtCountryOptional")}
                                                </option>
                                                {commonData.getCountryCodeList().map((item: any, idx: number) => (
                                                    <option key={idx} value={item.code}>
                                                        {getCurrentLangName(item.names)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    <span className="text-red-500">*</span>
                                                    {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupEssential.txtFirstName")}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={studentParams.firstName}
                                                    onChange={(evt) => onChangeInput("firstName", evt.target.value)}
                                                    className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688] text-base transition-colors"
                                                    placeholder={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupEssential.txtFirstNamePlaceholder")}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    <span className="text-red-500">*</span>
                                                    {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupEssential.txtLastName")}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={studentParams.lastName}
                                                    onChange={(evt) => onChangeInput("lastName", evt.target.value)}
                                                    className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688] text-base transition-colors"
                                                    placeholder={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupEssential.txtLastNamePlaceholder")}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                <span className="text-red-500">*</span>
                                                {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupEssential.txtBirthday")}
                                            </label>
                                            <input
                                                type="date"
                                                value={studentParams.birthday}
                                                onChange={(evt) => onChangeInput("birthday", evt.target.value)}
                                                aria-required="true"
                                                className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688] text-base transition-colors"
                                            />
                                        </div>
                                        {checkIsUnder14(studentParams.birthday) && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    <span className="text-red-500">*</span>
                                                    {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupEssential.txtParentEmail")}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={parentEmail}
                                                    onChange={(evt) => setParentEmail(evt.target.value.trim())}
                                                    className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688] text-base transition-colors"
                                                    placeholder={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupEssential.txtParentEmailPlaceholder")}
                                                />
                                            </div>
                                        )}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                <span className="text-red-500">*</span>
                                                {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupEssential.txtGender")}
                                            </label>
                                            <div className="flex gap-6">
                                                <label className="inline-flex items-center">
                                                    <input
                                                        type="radio"
                                                        name="genderTypeCode"
                                                        value="genderType:girl"
                                                        checked={studentParams.genderTypeCode === "genderType:girl"}
                                                        onChange={(evt) => onChangeInput("genderTypeCode", evt.target.value)}
                                                        className="h-4 w-4 text-[#CF3688] focus:ring-[#CF3688]"
                                                    />
                                                    <span className="ml-2 text-gray-700">
                                                        {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupEssential.txtGirl")}
                                                    </span>
                                                </label>
                                                <label className="inline-flex items-center">
                                                    <input
                                                        type="radio"
                                                        name="genderTypeCode"
                                                        value="genderType:boy"
                                                        checked={studentParams.genderTypeCode === "genderType:boy"}
                                                        onChange={(evt) => onChangeInput("genderTypeCode", evt.target.value)}
                                                        className="h-4 w-4 text-[#CF3688] focus:ring-[#CF3688]"
                                                    />
                                                    <span className="ml-2 text-gray-700">
                                                        {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupEssential.txtBoy")}
                                                    </span>
                                                </label>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAdditional.txtNickname")}
                                            </label>
                                            <p className="text-sm text-gray-500 mb-2">
                                                {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAdditional.txtNicknameDesc")}
                                            </p>
                                            <input
                                                type="text"
                                                value={studentParams.nickname}
                                                onChange={(evt) => onChangeInput("nickname", evt.target.value)}
                                                className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688] text-base"
                                                placeholder={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAdditional.txtNicknamePlaceholder")}
                                            />
                                        </div>
                                        <div>
                                            <label
                                                htmlFor="student-signup-handler-input"
                                                className="block text-sm font-medium text-gray-700 mb-2"
                                            >
                                                <span className="text-red-500">*</span>
                                                {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.handlerSectionTitle")}
                                            </label>
                                            <p className="text-sm text-gray-500 mb-2">
                                                {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.handlerSectionDesc")}
                                            </p>
                                            <p className="text-sm text-gray-500 mb-2 break-all">
                                                {handlerProfileUrlPrefix}
                                                <span className="text-gray-900 font-medium">{studentParams.slug || "…"}</span>
                                            </p>
                                            <input
                                                id="student-signup-handler-input"
                                                type="text"
                                                value={studentParams.slug}
                                                onChange={(evt) => onChangeInput("slug", evt.target.value)}
                                                onBlur={(evt) => {
                                                    void onBlurProfileSlug(evt.target.value);
                                                }}
                                                className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688] text-base transition-colors"
                                                placeholder={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.handlerInputPlaceholder")}
                                            />
                                            {profileSlugValidationMessage && (
                                                <p
                                                    className={`mt-2 text-sm ${profileSlugValidationType === "success"
                                                        ? "text-green-600"
                                                        : profileSlugValidationType === "checking"
                                                          ? "text-gray-500"
                                                          : "text-red-500"
                                                        }`}
                                                >
                                                    {profileSlugValidationMessage}
                                                </p>
                                            )}
                                            <p className="text-sm text-gray-500 mt-2">
                                                {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.handlerSectionHint")}
                                            </p>
                                        </div>
                                    </div>
                                </>
                            )}
                            {renderFooter({
                                showBack: !isInfoRegistrationFlow,
                                primaryLabel: getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.btnNext"),
                                primaryDisabled: isOnNetworking || !isProfileNextEnabled,
                                onPrimary: () => {
                                    void onWizardNext();
                                },
                            })}
                        </>
                    )}

                    {currentStep === "basic" && (
                        <>
                            {sectionShell(
                                <>
                                    <div className="space-y-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAdditional.txtHeight")}
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    value={studentParams.height}
                                                    onChange={(evt) => onChangeInput("height", evt.target.value)}
                                                    className="block w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688] text-base"
                                                    placeholder={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAdditional.txtHeightPlaceholder")}
                                                    onKeyPress={(evt) => {
                                                        if (!/[0-9]/.test(evt.key)) {
                                                            evt.preventDefault();
                                                        }
                                                    }}
                                                />
                                                <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                                                    <span className="text-gray-500">cm</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAdditional.txtWeight")}
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    value={studentParams.weight}
                                                    onChange={(evt) => onChangeInput("weight", evt.target.value)}
                                                    className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688] text-base"
                                                    placeholder={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAdditional.txtWeightPlaceholder")}
                                                    onKeyPress={(evt) => {
                                                        if (!/[0-9]/.test(evt.key)) {
                                                            evt.preventDefault();
                                                        }
                                                    }}
                                                />
                                                <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                                                    <span className="text-gray-500">kg</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAdditional.txtShoeSize")}
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    value={studentParams.shoeSize}
                                                    onChange={(evt) => onChangeInput("shoeSize", evt.target.value)}
                                                    className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688] text-base"
                                                    placeholder={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAdditional.txtShoeSizePlaceholder")}
                                                    onKeyPress={(evt) => {
                                                        if (!/[0-9]/.test(evt.key)) {
                                                            evt.preventDefault();
                                                        }
                                                    }}
                                                />
                                                <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                                                    <span className="text-gray-500">mm</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAdditional.txtMbti")}
                                            </label>
                                            <select
                                                value={studentParams.mbtiCode}
                                                onChange={(evt) => onChangeInput("mbtiCode", evt.target.value)}
                                                className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688] text-base"
                                            >
                                                <option value="" disabled>
                                                    {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAdditional.txtMbtiOptional")}
                                                </option>
                                                <option value="">{getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAdditional.txtNotSetOptional")}</option>
                                                {commonData.getMbtiCodeList().map((item: any, idx: number) => (
                                                    <option key={idx} value={item.code}>
                                                        {item.names.en}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAdditional.txtBloodType")}
                                            </label>
                                            <select
                                                value={studentParams.bloodTypeCode}
                                                onChange={(evt) => onChangeInput("bloodTypeCode", evt.target.value)}
                                                className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688] text-base"
                                            >
                                                <option value="">{getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAdditional.txtNotSetOptional")}</option>
                                                {["A", "B", "O", "AB"].map((type) => (
                                                    <option key={type} value={`bloodType:${type.toLowerCase()}`}>
                                                        {type}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </>
                            )}
                            {renderFooter({
                                showBack: true,
                                primaryLabel: getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.btnNext"),
                                onPrimary: () => {
                                    void onWizardNext();
                                },
                            })}
                        </>
                    )}

                    {currentStep === "specialty" && (
                        <>
                            {sectionShell(
                                <>
                                    <div className="space-y-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAdditional.txtSpecialty")}
                                            </label>
                                            <div className="space-y-2 flex flex-wrap gap-4">
                                                {specialtyList.map((specialty: any) => (
                                                    <label key={specialty.code} className="inline-flex items-center gap-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedSpecialtyCodes.includes(specialty.code)}
                                                            onChange={(evt) => onChangeCheckbox("specialty", specialty.code, evt.target.checked)}
                                                            className="h-4 w-4 text-[#CF3688] focus:ring-[#CF3688] rounded"
                                                        />
                                                        <span className="leading-none">{getCurrentLangName(specialty.names)}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex items-center justify-between gap-2 mb-3">
                                                <label className="block text-sm font-medium text-gray-700">
                                                    {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.txtUsedLanguages")}
                                                </label>
                                                <button
                                                    type="button"
                                                    onClick={addLanguageRow}
                                                    aria-label={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.btnAddLanguage")}
                                                    title={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.btnAddLanguage")}
                                                    className="h-8 w-8 shrink-0 rounded-full border border-[#CF3688] text-[#CF3688] hover:bg-pink-50 flex items-center justify-center"
                                                >
                                                    <span className="text-lg leading-none">+</span>
                                                </button>
                                            </div>
                                            {languageRows.length === 0 && (
                                                <p className="text-xs text-gray-500 mb-3">
                                                    {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.txtLanguagesEmptyHint")}
                                                </p>
                                            )}
                                            <div className="space-y-3">
                                                {languageRows.map((row: StudentLanguageRow) => (
                                                    <div key={row.id} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                                                        <select
                                                            value={row.languageCode}
                                                            onChange={(e) => updateLanguageRow(row.id, { languageCode: e.target.value })}
                                                            className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm"
                                                        >
                                                            <option value="">{getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.txtLanguage")}</option>
                                                            {langList.map((lang: any) => (
                                                                <option key={lang.code} value={lang.code}>
                                                                    {getCurrentLangName(lang.names)}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <select
                                                            value={row.level}
                                                            onChange={(e) => updateLanguageRow(row.id, { level: e.target.value })}
                                                            className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm"
                                                        >
                                                            {languageLevelList.map((lv: any) => (
                                                                <option key={lv.code} value={lv.code}>
                                                                    {getCurrentLangName(lv.names)}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeLanguageRow(row.id)}
                                                            aria-label={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.btnRemoveLanguage")}
                                                            title={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.btnRemoveLanguage")}
                                                            className="justify-self-end text-sm font-bold text-gray-600 whitespace-nowrap px-2 py-1 min-w-8 min-h-8 rounded-md hover:bg-gray-100"
                                                        >
                                                            -
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                            {renderFooter({
                                showBack: true,
                                primaryLabel: getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.btnNext"),
                                onPrimary: () => {
                                    void onWizardNext();
                                },
                            })}
                        </>
                    )}

                    {currentStep === "contact" && (
                        <>
                            {sectionShell(
                                <>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                <span className="text-red-500">*</span>
                                                {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupContact.txtContactType")}
                                            </label>
                                            <select
                                                value={studentParams.contactTypeCode}
                                                onChange={(evt) => onChangeInput("contactTypeCode", evt.target.value)}
                                                className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688]"
                                            >
                                                <option value="" disabled>
                                                    {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupContact.txtContactSelect")}
                                                </option>
                                                {commonData.getCommonCodeListByParent("contactType").map((item: any, idx: number) => (
                                                    <option key={idx} value={item.code}>
                                                        {getCurrentLangName(item.names)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                <span className="text-red-500">*</span>
                                                {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupContact.txtContactDetail")}
                                            </label>
                                            <input
                                                type="text"
                                                value={studentParams.contact}
                                                onChange={(evt) => onChangeInput("contact", evt.target.value)}
                                                placeholder={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupContact.txtContactDetailDesc")}
                                                className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688]"
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                            {renderFooter({
                                showBack: true,
                                primaryLabel: getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.btnNext"),
                                onPrimary: () => {
                                    void onWizardNext();
                                },
                            })}
                        </>
                    )}

                    {currentStep === "confirm" && (
                        <>
                            <div className="space-y-4 text-sm text-gray-800 mb-4">
                                <div className="border border-gray-100 rounded-xl p-4 bg-white shadow-sm">
                                            <h4 className="font-medium text-gray-900 mb-2 text-center">
                                                {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.txtPreviewSectionProfile")}
                                            </h4>
                                            <div className="mb-3 last:mb-0">
                                                <div className="text-gray-500">
                                                    {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.txtProfileImage")}
                                                </div>
                                                <div className="pl-4 pt-1 flex justify-center">
                                                    {imageInfo?.url ? (
                                                        <div className="h-48 w-48 shrink-0 overflow-hidden rounded-full border border-gray-200 bg-gray-50">
                                                            <img
                                                                src={imageInfo.url}
                                                                alt={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.txtProfileImage")}
                                                                className="h-full w-full object-cover"
                                                            />
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-900">{getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.txtPreviewNone")}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <ConfirmPreviewRow label={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupEssential.txtCountry")}>
                                                {countryItem ? getCurrentLangName(countryItem.names) : getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.txtPreviewNone")}
                                            </ConfirmPreviewRow>
                                            <ConfirmPreviewRow label={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupEssential.txtFirstName")}>
                                                {studentParams.firstName || "—"}
                                            </ConfirmPreviewRow>
                                            <ConfirmPreviewRow label={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupEssential.txtLastName")}>
                                                {studentParams.lastName || "—"}
                                            </ConfirmPreviewRow>
                                            <ConfirmPreviewRow label={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupEssential.txtBirthday")}>
                                                {studentParams.birthday || "—"}
                                            </ConfirmPreviewRow>
                                            <ConfirmPreviewRow label={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupEssential.txtGender")}>
                                                {studentParams.genderTypeCode === "genderType:girl"
                                                    ? getLanguageValue(PAGE_TYPE.COMMON, "studentSignupEssential.txtGirl")
                                                    : studentParams.genderTypeCode === "genderType:boy"
                                                      ? getLanguageValue(PAGE_TYPE.COMMON, "studentSignupEssential.txtBoy")
                                                      : "—"}
                                            </ConfirmPreviewRow>
                                            <ConfirmPreviewRow label={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAdditional.txtNickname")}>
                                                {studentParams.nickname || getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.txtPreviewNone")}
                                            </ConfirmPreviewRow>
                                            <ConfirmPreviewRow label={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.handlerSectionTitle")}>
                                                {studentParams.slug?.trim()
                                                    ? `${handlerProfileUrlPrefix}${studentParams.slug}`
                                                    : getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.txtPreviewNone")}
                                            </ConfirmPreviewRow>
                                </div>
                                <div className="border border-gray-100 rounded-xl p-4 bg-white shadow-sm">
                                            <h4 className="font-medium text-gray-900 mb-2 text-center">
                                                {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.txtPreviewSectionBasic")}
                                            </h4>
                                            <ConfirmPreviewRow label={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAdditional.txtHeight")}>
                                                {studentParams.height ? `${studentParams.height} cm` : "—"}
                                            </ConfirmPreviewRow>
                                            <ConfirmPreviewRow label={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAdditional.txtWeight")}>
                                                {studentParams.weight ? `${studentParams.weight} kg` : "—"}
                                            </ConfirmPreviewRow>
                                            <ConfirmPreviewRow label={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAdditional.txtShoeSize")}>
                                                {studentParams.shoeSize ? `${studentParams.shoeSize} mm` : "—"}
                                            </ConfirmPreviewRow>
                                            <ConfirmPreviewRow label={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAdditional.txtMbti")}>
                                                {mbtiItem?.names?.en || "—"}
                                            </ConfirmPreviewRow>
                                            <ConfirmPreviewRow label={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupAdditional.txtBloodType")}>
                                                {studentParams.bloodTypeCode?.replace("bloodType:", "").toUpperCase() || "—"}
                                            </ConfirmPreviewRow>
                                </div>
                                <div className="border border-gray-100 rounded-xl p-4 bg-white shadow-sm">
                                            <h4 className="font-medium text-gray-900 mb-2 text-center">
                                                {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.txtPreviewSectionSpecialtyInfo")}
                                            </h4>
                                            <ConfirmPreviewRow label={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.txtPreviewSectionSpecialty")}>
                                                {(selectedSpecialtyCodes
                                                    .map((code: string) => specialtyList.find((s: any) => s.code === code))
                                                    .filter(Boolean)
                                                    .map((item: any) => getCurrentLangName(item.names))
                                                    .join(", ")) || getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.txtPreviewNone")}
                                            </ConfirmPreviewRow>
                                            <ConfirmPreviewRow label={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.txtPreviewSectionLanguages")}>
                                                {languageRows.length === 0 ? (
                                                    getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.txtPreviewNone")
                                                ) : (
                                                    <ul className="list-disc space-y-1 pl-1">
                                                        {languageRows.map((row: StudentLanguageRow) => {
                                                            const lang = langList.find((l: any) => l.code === row.languageCode);
                                                            return (
                                                                <li key={row.id}>
                                                                    {lang ? getCurrentLangName(lang.names) : row.languageCode} —{" "}
                                                                    {proficiencyLabel(row.level, languageLevelList)}
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                )}
                                            </ConfirmPreviewRow>
                                </div>
                                <div className="border border-gray-100 rounded-xl p-4 bg-white shadow-sm">
                                            <h4 className="font-medium text-gray-900 mb-2 text-center">
                                                {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.txtPreviewSectionContact")}
                                            </h4>
                                            <ConfirmPreviewRow label={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupContact.txtContactType")}>
                                                {contactTypeItem ? getCurrentLangName(contactTypeItem.names) : "—"}
                                            </ConfirmPreviewRow>
                                            <ConfirmPreviewRow label={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupContact.txtContactDetail")}>
                                                {studentParams.contact || "—"}
                                            </ConfirmPreviewRow>
                                </div>
                            </div>
                            <div className="flex w-full flex-row items-center justify-between gap-3 pt-6">
                                <button
                                    type="button"
                                    onClick={onWizardBack}
                                    className="shrink-0 px-6 py-3 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                                >
                                    {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.btnBack")}
                                </button>
                                <button
                                    type="button"
                                    ref={btnRef}
                                    onClick={onClickBtnSignup}
                                    disabled={isOnNetworking}
                                    className="shrink-0 px-6 py-3 text-white bg-[#CF3688] rounded-xl hover:bg-[#B52E74] transition-colors disabled:opacity-50"
                                >
                                    {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupWizard.btnCompleteSignup")}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </main>
        </>
    );
};

export default StudentSignupComponent;
